<template>
    <GenericModal
        :modelValue="showModal"
        @update:modelValue="handleModalClose"
        :closable="true"
        :fullscreenMobile="true"
        :darkMode="true"
        contentPadding="0"
    >
        <template #header>
            <LanguageSelector :fullDetail="true" />
        </template>
        <div class="how-it-works-modal">
            <div class="modal-body">
                <h1 class="modal-title">
                    {{ $t('how_it_works.title') }}
                </h1>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ $t('how_it_works.steps.step1.title') }}
                            </h3>
                            <p class="step-description">
                                {{ $t('how_it_works.steps.step1.description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ $t('how_it_works.steps.step2.title') }}
                            </h3>
                            <p class="step-description">
                                {{ $t('how_it_works.steps.step2.description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ $t('how_it_works.steps.step3.title') }}
                            </h3>
                            <p class="step-description">
                                {{ $t('how_it_works.steps.step3.description') }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="bottom-section">
                    <div class="checkbox-container">
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="agreedToTerms"
                                class="checkbox-input"
                            />
                            <span
                                class="checkbox-text"
                                v-html="$t('how_it_works.age_confirmation')"
                            ></span>
                        </label>
                    </div>

                    <button
                        class="btn-primary"
                        :disabled="!agreedToTerms || isLoading"
                        @click="handleContinue"
                    >
                        {{
                            isLoading
                                ? $t('general.loading')
                                : $t('how_it_works.continue')
                        }}
                    </button>
                </div>
            </div>
        </div>
    </GenericModal>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import GenericModal from '@/components/shared/core/GenericModal.vue'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'

// Router
const router = useRouter()

// State
const showModal = ref(false)
const agreedToTerms = ref(true)
const isLoading = ref(false)

// Methods
const handleContinue = async () => {
    if (agreedToTerms.value) {
        showModal.value = false
        router.push('/mistresses')
    }
}

const handleModalClose = (value) => {
    showModal.value = value
    // Reset checkbox when modal is closed
    if (!value) {
        agreedToTerms.value = true
    }
}

// Expose methods for external control
defineExpose({
    show: () => {
        showModal.value = true
        agreedToTerms.value = true
    },
    hide: () => {
        showModal.value = false
        agreedToTerms.value = true
    },
})
</script>

<style scoped>
.how-it-works-modal {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: white;
    position: relative;
    overflow: hidden;
}

.modal-body {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center; /* Center vertically */
    text-align: left;
    padding: 120px 40px 40px;
    gap: 60px;
    max-width: 400px;
    margin: 0 auto;
    height: 100%;
    overflow-y: auto;
}

.modal-title {
    font-family: 'Meditative', serif;
    font-weight: 400;
    font-size: 38px;
    line-height: 80%;
    color: white;
    text-wrap: balance;
    margin: 0;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.steps-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 100%;
}

.step-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.step-number {
    font-family: 'Nunito', sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: white;
    line-height: 1.2;
    min-width: 20px;
}

.step-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.step-title {
    font-family: 'Nunito', sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 1.2;
    color: white;
    margin: 0;
}

.step-description {
    font-family: 'Nunito', sans-serif;
    font-weight: 300;
    font-size: 14px;
    line-height: 1.1;
    color: rgba(255, 255, 255, 0.55);
    margin: 0;
    text-wrap-style: balance;
}

.bottom-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: auto; /* Push to bottom if space permits */
}

.checkbox-container {
    width: 100%;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    text-align: left;
}

.checkbox-input {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    flex-shrink: 0;
    accent-color: #9b6e2b;
}

.checkbox-text {
    font-family: 'Nunito', sans-serif;
    font-size: 12px;
    line-height: 15px;
    color: white;
    font-weight: 300;
}

.checkbox-text :deep(a) {
    color: white;
    text-decoration: underline;
}

.btn-primary {
    width: 100%;
    padding: 16px;
    border-radius: 12px;
    font-weight: 600;
    border: none;
    transition: all 0.3s;
    text-align: center;
    font-size: 14px;
    font-family: 'Basier Circle', sans-serif;
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    background: #9b6e2b;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Desktop styles */
@media (min-width: 768px) {
    .modal-body {
        max-width: 450px;
        padding: 60px 40px;
    }

    .modal-title {
        font-size: 56px;
    }

    .step-title {
        font-size: 20px;
    }

    .step-description {
        font-size: 16px;
    }
}
</style>
